import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useParams, useNavigate } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { smart_meters_base_url, customer_account_base_url } from "../../../../../../utils/urls";

const CustomerDetailsTab = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        customerId: '',
        userType: '',
        balanceBF: 0
    });
    const { meterId } = useParams();
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
    const [showChangePaymentTypeDialog, setShowChangePaymentTypeDialog] = useState(false);
    const [showResendOnboardingDialog, setShowResendOnboardingDialog] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [customerAccounts, setCustomerAccounts] = useState([]); 
    const [accountBalances, setAccountBalances] = useState({}); // Store balances for each account
    const [negativeBalances, setNegativeBalances] = useState({}); // Store negative balances for each account
    const [balanceLoading, setBalanceLoading] = useState({}); // Track loading state for each balance
    const [loading, setLoading] = useState(false);
    const [meterDetails, setMeterDetails] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [newPaymentType, setNewPaymentType] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [resendPhone, setResendPhone] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const paymentOptions = [
        { label: 'Prepaid', value: 'prepaid' },
        { label: 'Postpaid', value: 'postpaid' }
    ];

    // Fetch meter details and customer accounts when component mounts
    useEffect(() => {
        if (meterId && selectedFacilityId) {
            fetchMeterDetails();
        }
    }, [meterId, selectedFacilityId]);

    // Prepopulate the form when assigning a customer
    useEffect(() => {
        if (showAssignModal && customerAccounts[0]) {
            setFormData({
                customerId: customerAccounts[0].customerId || '',
                userType: customerAccounts[0].payment_type?.toLowerCase() || '',
                balanceBF: customerAccounts[0].balanceBF || 0
            });
        }
    }, [showAssignModal, customerAccounts]);

    useEffect(() => {
        if (showAssignModal && selectedFacilityId) {
            fetchCustomers();
        }
    }, [showAssignModal, selectedFacilityId]);

    // Fetch customer accounts when meter details are loaded
    useEffect(() => {
        if (meterDetails?.meterNumber && selectedFacilityId) {
            fetchCustomerAccounts(meterDetails.meterNumber);
        }
    }, [meterDetails, selectedFacilityId]);

    // Fetch balances for all customer accounts
    useEffect(() => {
        if (customerAccounts.length > 0) {
            customerAccounts.forEach(account => {
                if (account._id && account.payment_type?.toLowerCase() === 'prepaid') {
                    fetchAccountBalance(account._id);
                }
            });
        }
    }, [customerAccounts]);

    // Fetch meter details
    const fetchMeterDetails = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${smart_meters_base_url}/details/${meterId}`,
                'GET'
            );
            if (response.success) {
                setMeterDetails(response.data.meter);
            } else {
                toastify('Failed to fetch meter details', 'error');
            }
        } catch (error) {
            toastify('Error fetching meter details', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Fetch customer accounts for this meter
    const fetchCustomerAccounts = async (meterNumber) => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${customer_account_base_url}/get_accounts_by_meter/${selectedFacilityId}/${meterNumber}`,
                'GET'
            );

            if (response.success && response.data) {
                // Ensure we're always setting an array
                const accounts = Array.isArray(response.data) ? response.data :
                    (response.data.data && Array.isArray(response.data.data)) ? response.data.data : [];
                setCustomerAccounts(accounts);
            } else {
                setCustomerAccounts([]);
            }
        } catch (error) {
            setCustomerAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch account balance for a specific account
    const fetchAccountBalance = async (accountId) => {
        try {
            setBalanceLoading(prev => ({ ...prev, [accountId]: true }));
            const response = await makeRequest2(
                `${customer_account_base_url}/get_accounts_balance/${accountId}`,
                'GET'
            );

            if (response.success) {
                const balanceData = response.data.data || {};
                setAccountBalances(prev => ({
                    ...prev,
                    [accountId]: balanceData.currentBalance || 0.0
                }));
                // Store negative balance separately
                setNegativeBalances(prev => ({
                    ...prev,
                    [accountId]: balanceData.negativeBalance || 0.0
                }));
            } else {
                setAccountBalances(prev => ({
                    ...prev,
                    [accountId]: 0.0
                }));
                setNegativeBalances(prev => ({
                    ...prev,
                    [accountId]: 0.0
                }));
            }
        } catch (error) {
            setAccountBalances(prev => ({
                ...prev,
                [accountId]: 0.0
            }));
            setNegativeBalances(prev => ({
                ...prev,
                [accountId]: 0.0
            }));
        } finally {
            setBalanceLoading(prev => ({ ...prev, [accountId]: false }));
        }
    };

    const renderNegativeBalance = (account) => {
        const accountId = account._id;
        const isLoading = balanceLoading[accountId];
        const negativeBalance = negativeBalances[accountId];

        if (account.payment_type?.toLowerCase() !== 'prepaid') {
            return <span className="text-muted">N/A</span>;
        }

        if (isLoading) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted">Loading...</span>
                </div>
            );
        }

        const formattedNegativeBalance = typeof negativeBalance === 'number'
            ? new Intl.NumberFormat('en-KE', {
                style: 'currency',
                currency: 'KES'
            }).format(negativeBalance)
            : 'KES 0.00';

        return (
            <div className="d-flex align-items-center">
                <span className={negativeBalance > 0 ? 'text-danger' : 'text-muted'}>
                    {formattedNegativeBalance}
                </span>
                <button
                    className="btn btn-link btn-sm p-0 ms-2"
                    onClick={() => refreshAccountBalance(accountId)}
                    disabled={isLoading}
                    title="Refresh balance"
                >
                    <i className="pi pi-refresh"></i>
                </button>
            </div>
        );
    };

    // Function to refresh balance for a specific account
    const refreshAccountBalance = (accountId) => {
        fetchAccountBalance(accountId);
    };

    // Fetch list of customers for the facility
    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `/api/app/customer_management/get_customers/${selectedFacilityId}`,
                'GET'
            );
            if (response.success) {
                const customersData = response.data?.customers || response.data || [];
                setCustomers(customersData);
            } else {
                toastify('Failed to fetch customers', 'error');
            }
        } catch (error) {
            toastify('Error loading customers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle resend onboarding
    const handleResendOnboarding = async () => {
        if (!selectedAccount) {
            toastify('Account information not found', 'error');
            return;
        }

        // Validate email and phone
        if (!resendEmail && !resendPhone) {
            toastify('Please provide at least one contact method (email or phone)', 'error');
            return;
        }

        // Simple email validation
        if (resendEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail)) {
            toastify('Please enter a valid email address', 'error');
            return;
        }

        // Simple phone validation (at least 10 digits)
        if (resendPhone && !/^\d{10,}$/.test(resendPhone.replace(/\D/g, ''))) {
            toastify('Please enter a valid phone number (at least 10 digits)', 'error');
            return;
        }

        setResendLoading(true);
        try {
            // Prepare the data for resend onboarding
            const onboardingData = {
                accountId: selectedAccount._id,
                accountNumber: selectedAccount.account_no,
                customerName: selectedAccount.customerName,
                meterNumber: selectedAccount.meterNumber,
                facilityId: selectedFacilityId,
                email: resendEmail || selectedAccount.email,
                phoneNumber: resendPhone || selectedAccount.phoneNumber,
                // Include any other necessary data for onboarding
                paymentType: selectedAccount.payment_type,
                unitName: selectedAccount.unitName
            };

            // Call the API to resend onboarding
            const response = await makeRequest2(
                `${customer_account_base_url}/customer_account/resend_onboarding`,
                'POST',
                onboardingData
            );

            if (response.success) {
                toastify('Onboarding email/SMS sent successfully!', 'success');
                
                // Update the account with new contact info if provided
                if (resendEmail || resendPhone) {
                    const updateData = {};
                    if (resendEmail) updateData.email = resendEmail;
                    if (resendPhone) updateData.phoneNumber = resendPhone;
                    
                    await makeRequest2(
                        `${customer_account_base_url}/update_contact/${selectedAccount._id}`,
                        'PUT',
                        updateData
                    );
                    
                    // Refresh customer accounts to show updated info
                    if (meterDetails?.meterNumber) {
                        fetchCustomerAccounts(meterDetails.meterNumber);
                    }
                }
                
                setShowResendOnboardingDialog(false);
                setResendEmail('');
                setResendPhone('');
            } else {
                toastify(response.error || 'Failed to send onboarding information', 'error');
            }
        } catch (error) {
            toastify('Error sending onboarding information', 'error');
        } finally {
            setResendLoading(false);
        }
    };

    // Prepare resend onboarding dialog
    const prepareResendOnboarding = (account) => {
        setSelectedAccount(account);
        setResendEmail(account.email || '');
        setResendPhone(account.phoneNumber || '');
        setShowResendOnboardingDialog(true);
    };

    const handleSubmit = async () => {
        if (!formData.customerId) {
            toastify('Please select a customer', 'error');
            return;
        }
        if (!formData.userType) {
            toastify('Please select payment method', 'error');
            return;
        }

        if (!meterDetails) {
            toastify('Meter details not available. Please refresh the page.', 'error');
            return;
        }

        setLoading(true);
        try {
            // 1. Update the smart meter with the assignment data
            const meterUpdateData = {
                ...formData,
                // Add any other fields needed for meter update
            };

            const meterResponse = await makeRequest2(
                `${smart_meters_base_url}/${meterId}`,
                'PUT',
                meterUpdateData
            );

            if (!meterResponse.success) {
                toastify(meterResponse.error || 'Failed to update smart meter', 'error');
                return;
            }

            // 2. Build account data using the meter response and customer details
            const selectedCustomer = customers.find(cust => cust._id === formData.customerId);

            // Use the meter details to get account number and meter number
            const accountData = {
                facilityId: selectedFacilityId,
                account_no: meterDetails.accountNumber || meterResponse.data?.accountNumber || `ACC-${Date.now()}`,
                meterNumber: meterDetails.meterNumber || meterResponse.data?.meterNumber || `MTR-${Date.now()}`,
                unitId: meterDetails.unitId,
                customerId: formData.customerId,
                customerName: selectedCustomer
                    ? (selectedCustomer.fullName || `${selectedCustomer.firstName} ${selectedCustomer.lastName}`)
                    : 'Unknown',
                phoneNumber: selectedCustomer ? selectedCustomer.phoneNumber : 'N/A',
                email: selectedCustomer ? selectedCustomer.email : 'N/A',
                payment_type: formData.userType === 'prepaid' ? 'Prepaid' : 'Postpaid',
                previousReading: meterDetails.initialReading || 0,
                currentReading: meterDetails.currentReading || 0,
                meter_id: meterId,
                balanceBF: formData.balanceBF || 0,
                status: 'Active'
            };

            // 3. Create the customer account
            const accountResponse = await makeRequest2(
                `${customer_account_base_url}/add`,
                'POST',
                accountData
            );

            if (!accountResponse.success) {
                // If account creation fails, revert the smart meter update
                await makeRequest2(
                    `${smart_meters_base_url}/${meterId}`,
                    'PUT',
                    { customerId: null }
                );
                toastify(
                    accountResponse.error || 'Failed to create customer account. Changes have been reverted.',
                    'error'
                );
                return;
            }

            handleCloseModal();
            // Re-fetch updated meter data and customer accounts
            fetchMeterDetails();
            if (meterDetails?.meterNumber) {
                fetchCustomerAccounts(meterDetails.meterNumber);
            }
            toastify('Customer assigned successfully', 'success');
        } catch (error) {
            toastify('Error assigning customer', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setFormData({
            customerId: '',
            userType: '',
            balanceBF: 0
        });
        setShowAssignModal(false);
    };

    // Function to prepare for deactivation
    const prepareDeactivateAccount = (account) => {
        setSelectedAccount(account);
        setShowDeactivateDialog(true);
    };

    // Function to handle deactivating an account
    const handleDeactivateAccount = async () => {
        if (!selectedAccount || !selectedAccount.account_no) {
            toastify('Account information not found', 'error');
            return;
        }

        const account_no = selectedAccount.account_no;

        setLoading(true);
        try {
            // 1. Deactivate the customer account - now including facilityId in the URL
            const accountResponse = await makeRequest2(
                `${customer_account_base_url}/deactivate/${selectedFacilityId}/${meterId}`,
                'PUT',
                { accountId: selectedAccount._id, account_no }
            );

            if (!accountResponse.success) {
                toastify(accountResponse.error || 'Failed to deactivate account', 'error');
                return;
            }

            const meterUpdateData = { customerId: null };
            const meterResponse = await makeRequest2(
                `${smart_meters_base_url}/${meterId}`,
                'PUT',
                meterUpdateData
            );
            if (!meterResponse.success) {
                toastify(meterResponse.error || 'Failed to update smart meter', 'error');
                return;
            }

            toastify('Account deactivated successfully', 'success');
            // Re-fetch meter data and customer accounts
            fetchMeterDetails();
            if (meterDetails?.meterNumber) {
                fetchCustomerAccounts(meterDetails.meterNumber);
            }
        } catch (error) {
            toastify('Error during deactivation process', 'error');
        } finally {
            setLoading(false);
            setShowDeactivateDialog(false);
            setSelectedAccount(null);
        }
    };

    // Function to handle activating an account
    const handleActivateAccount = async (account) => {
        if (!account || !account.account_no) {
            toastify('Account information not found', 'error');
            return;
        }

        setLoading(true);
        try {
            // Activate the customer account
            const accountResponse = await makeRequest2(
                `${customer_account_base_url}/activate/${selectedFacilityId}/${meterId}`,
                'PUT',
                { accountId: account._id, account_no: account.account_no }
            );

            if (!accountResponse.success) {
                toastify(accountResponse.error || 'Failed to activate account', 'error');
                return;
            }

            // Update the meter with the customer ID
            const meterUpdateData = { customerId: account.customerId };
            const meterResponse = await makeRequest2(
                `${smart_meters_base_url}/${meterId}`,
                'PUT',
                meterUpdateData
            );

            if (!meterResponse.success) {
                toastify(meterResponse.error || 'Failed to update smart meter', 'error');
                return;
            }

            toastify('Account activated successfully', 'success');
            // Re-fetch meter data and customer accounts
            fetchMeterDetails();
            if (meterDetails?.meterNumber) {
                fetchCustomerAccounts(meterDetails.meterNumber);
            }
        } catch (error) {
            toastify('Error during activation process', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Function to handle payment type change
    const handleChangePaymentType = async () => {
        if (!selectedAccount || !selectedAccount.account_no) {
            toastify('Account information not found', 'error');
            return;
        }

        if (!newPaymentType) {
            toastify('Please select a payment type', 'error');
            return;
        }

        setLoading(true);
        try {
            // Update the payment type for the account
            const accountResponse = await makeRequest2(
                `${customer_account_base_url}/update_payment_type/${selectedAccount.account_no}`,
                'PUT',
                { payment_type: newPaymentType === 'prepaid' ? 'Prepaid' : 'Postpaid' }
            );

            if (!accountResponse.success) {
                toastify(accountResponse.error || 'Failed to update payment type', 'error');
                return;
            }

            toastify('Payment type updated successfully', 'success');
            // Re-fetch customer accounts
            if (meterDetails?.meterNumber) {
                fetchCustomerAccounts(meterDetails.meterNumber);
            }
        } catch (error) {
            toastify('Error updating payment type', 'error');
        } finally {
            setLoading(false);
            setShowChangePaymentTypeDialog(false);
            setNewPaymentType('');
            setSelectedAccount(null);
        }
    };

    // Function to handle navigation to customer account details
    const handleViewCustomerAccount = (account) => {
        if (!account || !account._id) {
            toastify('Account information not found', 'error');
            return;
        }

        const isPostpaid = account.payment_type?.toLowerCase() === 'postpaid';
        const path = isPostpaid
            ? '/facility/utility_management/meter_management/water_meters/view_customer_account'
            : `/facility/utility_management/meter_management/water_meters/view_meter_tabs/customer_account/view_customer_details/${account._id}`;
        navigate(path, { state: { accountId: account._id } });
    };

    const formatCustomerName = (cust) => {
        if (cust.firstName && cust.lastName) return `${cust.firstName} ${cust.lastName}`;
        if (cust.fullName) return cust.fullName;
        if (cust.name) return cust.name;
        return 'Unnamed Customer';
    };

    // Build searchable options for the customer dropdown: one flat, filterable
    // list combining name, phone, and email so the Dropdown's built-in filter
    // (which narrows the panel's list as you type) can match on any of them.
    const customerOptions = useMemo(() => {
        return customers.map((cust) => ({
            _id: cust._id,
            displayName: formatCustomerName(cust),
            phoneNumber: cust.phoneNumber || '',
            email: cust.email || '',
            raw: cust
        }));
    }, [customers]);

    // Custom row shown inside the dropdown panel for each customer
    const customerOptionTemplate = (option) => {
        if (!option) return null;
        return (
            <div className="d-flex flex-column">
                <span>{option.displayName}</span>
                <small className="text-muted">{option.phoneNumber || 'No phone'}</small>
            </div>
        );
    };

    // What shows in the closed field once a customer is selected
    const customerValueTemplate = (option) => {
        if (!option) {
            return <span className="p-dropdown-label-placeholder">Search by name, phone, or email</span>;
        }
        return (
            <span>
                {option.displayName}{option.phoneNumber ? ` — ${option.phoneNumber}` : ''}
            </span>
        );
    };

    // Function to render balance with loading state
    const renderBalance = (account) => {
        const accountId = account._id;
        const isLoading = balanceLoading[accountId];
        const balance = accountBalances[accountId];

        if (account.payment_type?.toLowerCase() !== 'prepaid') {
            return <span className="text-muted">N/A</span>;
        }

        if (isLoading) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted">Loading...</span>
                </div>
            );
        }

        const formattedBalance = typeof balance === 'number'
            ? new Intl.NumberFormat('en-KE', {
                style: 'currency',
                currency: 'KES'
            }).format(balance)
            : 'KES 0.00';

        return (
            <div className="d-flex align-items-center">
                <span className={balance < 0 ? 'text-danger' : 'text-success'}>
                    {formattedBalance}
                </span>
                <button
                    className="btn btn-link btn-sm p-0 ms-2"
                    onClick={() => refreshAccountBalance(accountId)}
                    disabled={isLoading}
                    title="Refresh balance"
                >
                    <i className="pi pi-refresh"></i>
                </button>
            </div>
        );
    };

    // Render actions dropdown for each account
    const renderActionDropdown = (account) => {
        const items = [
            // Add view account option
            {
                label: 'View Account',
                icon: 'pi pi-eye',
                command: () => handleViewCustomerAccount(account)
            },
            // Add resend onboarding option
            {
                label: 'Resend Onboarding',
                icon: 'pi pi-send',
                command: () => prepareResendOnboarding(account)
            }
        ];

        // Add actions based on account status
        if (account.status === 'Active') {
            items.push({
                label: 'Deactivate Account',
                icon: 'pi pi-times-circle',
                command: () => prepareDeactivateAccount(account)
            });
        } else if (account.status === 'Inactive') {
            items.push({
                label: 'Activate Account',
                icon: 'pi pi-check-circle',
                command: () => handleActivateAccount(account)
            });
        }

        // Always allow changing payment type
        items.push({
            label: 'Change Payment Type',
            icon: 'pi pi-sync',
            command: () => {
                setSelectedAccount(account);
                setNewPaymentType(account.payment_type?.toLowerCase() === 'prepaid' ? 'prepaid' : 'postpaid');
                setShowChangePaymentTypeDialog(true);
            }
        });

        return (
            <Dropdown
                value={null}
                options={items}
                onChange={(e) => {
                    if (e.value && e.value.command) {
                        e.value.command();
                    }
                }}
                placeholder="Actions"
                className="p-button-sm p-button-secondary"
                optionLabel="label"
            />
        );
    };

    // Updated function: Redesigned table with badges, balance, and actions column
    const renderCustomerInfo = () => {
        if (customerAccounts.length === 0) {
            return (
                <div className="alert alert-info">
                    No customer accounts found for this meter. Click "Assign Customer" to create one.
                </div>
            );
        }

        return (
            <div className="table-responsive">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Account Number</th>
                            <th>Customer</th>
                            <th>Phone Number</th>
                            <th>Unit</th>
                            <th>Payment Type</th>
                            <th>Current Balance</th>
                            <th>Negative Balance</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {customerAccounts.map((account, index) => (
                            <tr key={account._id || index}>
                                <td>{account.account_no || 'N/A'}</td>
                                <td>{account.customerName || 'N/A'}</td>
                                <td>{account.phoneNumber || 'N/A'}</td>
                                <td>{account.unitName || 'N/A'}</td>
                                <td>
                                    <span className={`badge ${account.payment_type?.toLowerCase() === 'prepaid' ? 'bg-success' : 'bg-primary'}`}>
                                        {account.payment_type || 'N/A'}
                                    </span>
                                </td>
                                <td>
                                    {renderBalance(account)}
                                </td>
                                <td>
                                    {renderNegativeBalance(account)}
                                </td>
                                <td>
                                    <span className={`badge ${account.status === 'Active' ? 'bg-success text-light' : 'bg-danger text-light'}`}>
                                        {account.status || 'N/A'}
                                    </span>
                                </td>
                                <td>
                                    {renderActionDropdown(account)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderAssignModal = () => (
        <Dialog
            header="Assign Customer"
            visible={showAssignModal}
            onHide={handleCloseModal}
            style={{ width: '50vw' }}
            modal
            className="p-fluid assign-customer-dialog"
        >
            <div className="container py-2">
                {/* Customer selection - searchable dropdown: type to filter the list, click to select */}
                <div className="row mb-2">
                    <div className="col-12">
                        <label className="form-label text-sm mb-1">Customer</label>
                        <Dropdown
                            value={formData.customerId}
                            options={customerOptions}
                            optionLabel="displayName"
                            optionValue="_id"
                            onChange={(e) => handleInputChange('customerId', e.value)}
                            itemTemplate={customerOptionTemplate}
                            valueTemplate={(option) => {
                                const selected = customerOptions.find(c => c._id === formData.customerId);
                                return customerValueTemplate(selected);
                            }}
                            filter
                            filterBy="displayName,phoneNumber"
                            filterPlaceholder="Search by name or phone"
                            filterInputAutoFocus
                            emptyFilterMessage="No matching customers"
                            placeholder="Search and select a customer"
                            className="w-100"
                            disabled={loading}
                            showClear
                        />
                    </div>
                </div>

                {/* Payment Method */}
                <div className="row mb-2">
                    <div className="col-md-6">
                        <label className="form-label text-sm mb-1">Choose Billing Method</label>
                        <select
                            className="form-select form-select-sm"
                            value={formData.userType}
                            onChange={(e) => handleInputChange('userType', e.target.value)}
                            disabled={loading}
                        >
                            <option value="">Choose one option</option>
                            {paymentOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Balance B/F */}
                    <div className="row mb-2">
                        <div className="col-md-6">
                            <label className="form-label text-sm mb-1">Balance B/F</label>
                            <InputText
                                type="number"
                                className="form-control form-control-sm"
                                value={formData.balanceBF}
                                onChange={(e) => handleInputChange('balanceBF', e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Close"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleCloseModal}
                        disabled={loading}
                    />
                    <Button
                        label={loading ? 'Submitting...' : 'Submit'}
                        className="btn btn-primary btn-sm"
                        onClick={handleSubmit}
                        disabled={loading}
                    />
                </div>
            </div>
        </Dialog>
    );

    // Confirmation dialog for deactivation
    const renderDeactivateDialog = () => (
        <Dialog
            header="Confirm Deactivation"
            visible={showDeactivateDialog}
            onHide={() => setShowDeactivateDialog(false)}
            style={{ width: '30vw' }}
            modal
        >
            <div>
                <p>Are you sure you want to deactivate this account?</p>
                <p><strong>Account: </strong>{selectedAccount?.account_no}</p>
                <p><strong>Customer: </strong>{selectedAccount?.customerName}</p>
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancel"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowDeactivateDialog(false)}
                        disabled={loading}
                    />
                    <Button
                        label={loading ? 'Processing...' : 'Confirm'}
                        className="btn btn-danger btn-sm"
                        onClick={handleDeactivateAccount}
                        disabled={loading}
                    />
                </div>
            </div>
        </Dialog>
    );

    // Dialog for changing payment type
    const renderChangePaymentTypeDialog = () => (
        <Dialog
            header="Change Payment Type"
            visible={showChangePaymentTypeDialog}
            onHide={() => setShowChangePaymentTypeDialog(false)}
            style={{ width: '30vw' }}
            modal
        >
            <div>
                <p>Select new payment type for account: {selectedAccount?.account_no}</p>
                <div className="mb-3">
                    <select
                        className="form-select"
                        value={newPaymentType}
                        onChange={(e) => setNewPaymentType(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">-- Select Payment Type --</option>
                        {paymentOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancel"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowChangePaymentTypeDialog(false)}
                        disabled={loading}
                    />
                    <Button
                        label={loading ? 'Processing...' : 'Confirm'}
                        className="btn btn-primary btn-sm"
                        onClick={handleChangePaymentType}
                        disabled={loading}
                    />
                </div>
            </div>
        </Dialog>
    );

    // Dialog for resend onboarding
    const renderResendOnboardingDialog = () => (
        <Dialog
            header="Resend Onboarding Information"
            visible={showResendOnboardingDialog}
            onHide={() => setShowResendOnboardingDialog(false)}
            style={{ width: '40vw' }}
            modal
        >
            <div>
                <p>Resend onboarding information for account: <strong>{selectedAccount?.account_no}</strong></p>
                <p>Customer: <strong>{selectedAccount?.customerName}</strong></p>
                
                <div className="mb-3">
                    <label className="form-label">Email Address</label>
                    <InputText
                        type="email"
                        className="form-control"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        placeholder="Enter email address for onboarding"
                        disabled={resendLoading}
                    />
                    <small className="text-muted">Leave empty to use existing email: {selectedAccount?.email || 'Not available'}</small>
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Phone Number</label>
                    <InputText
                        type="tel"
                        className="form-control"
                        value={resendPhone}
                        onChange={(e) => setResendPhone(e.target.value)}
                        placeholder="Enter phone number for SMS"
                        disabled={resendLoading}
                    />
                    <small className="text-muted">Leave empty to use existing phone: {selectedAccount?.phoneNumber || 'Not available'}</small>
                </div>
                
                <div className="alert alert-info">
                    <i className="pi pi-info-circle me-2"></i>
                    Onboarding information will be sent via email and/or SMS. At least one contact method is required.
                </div>
                
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancel"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowResendOnboardingDialog(false)}
                        disabled={resendLoading}
                    />
                    <Button
                        label={resendLoading ? 'Sending...' : 'Send Onboarding'}
                        className="btn btn-primary btn-sm"
                        onClick={handleResendOnboarding}
                        disabled={resendLoading || (!resendEmail && !resendPhone)}
                    />
                </div>
            </div>
        </Dialog>
    );

    // Show loading spinner if data is being fetched
    if (loading && !meterDetails) {
        return (
            <div className="d-flex justify-content-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="row">
            <div className="col-sm-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                            <Button
                                label="Assign Customer"
                                className="btn btn-primary btn-sm"
                                onClick={() => setShowAssignModal(true)}
                            />
                        </div>
                    </div>
                    <div className="card-body">
                        {renderCustomerInfo()}
                    </div>
                </div>
            </div>
            {renderAssignModal()}
            {renderDeactivateDialog()}
            {renderChangePaymentTypeDialog()}
            {renderResendOnboardingDialog()}
        </div>
    );
};

export default CustomerDetailsTab;